import { useState, useEffect, useRef } from 'react';
import { Row, Col, Card, Statistic, Spin, Table, Tag, Button, Empty, message } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, ThunderboltOutlined, BulbOutlined, EnvironmentOutlined, CarOutlined, FullscreenOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import ReactEChartsCore from 'echarts-for-react';
import { dashboardApi, alarms } from '../../services/api';

const LEVEL_MAP = {
  '提示': 'info', '信息': 'info', info: 'info',
  '警告': 'warning', warning: 'warning',
  '严重': 'critical', critical: 'critical',
  '紧急': 'emergency', emergency: 'emergency',
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState(null);
  const [activeAlarms, setActiveAlarms] = useState([]);

  useEffect(() => {
    loadData();
    const timer = setInterval(loadData, 10000);
    return () => clearInterval(timer);
  }, []);

  const inFlightRef = useRef(false);

  const loadData = async () => {
    // 轮询竞态守卫：上一轮未返回时跳过本轮，避免慢请求重叠乱序
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const [overviewRes, alarmRes] = await Promise.all([
        dashboardApi.overview(),
        alarms.list({ page: 1, pageSize: 4 }),
      ]);
      setData(overviewRes.data);
      setTrend(overviewRes.data?.trend || null);
      // "活跃告警"只展示未处理/处理中，已解决(resolved)的历史告警不再混入
      setActiveAlarms((alarmRes.data || []).filter((a) => ['未处理', '处理中'].includes(a.status)));
    } catch (err) {
      message.error(err.message || '仪表盘数据加载失败');
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  };

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  if (!data) return <div>暂无数据</div>;

  const { overview, today, alarms: alarmStats, vpp } = data;

  // null = 无此类设备（如园区尚未接入市电/光伏/储能表计），显示"暂无数据"而非 0 或 null%
  // 注意：antd Statistic 内部会 String(value)，value 不能传 JSX，否则渲染成 [object Object]
  const sv = (v) => (v === null || v === undefined) ? '暂无数据' : v;
  const ss = (v, unit) => (v === null || v === undefined) ? '' : unit;
  const svStyle = (v, color) => (v === null || v === undefined) ? { fontSize: 15, color: '#999' } : { color };

  const hasTrend = trend && Object.values(trend.series || {}).some((arr) => arr.some((v) => v != null));

  // 负荷/光伏/储能的组合图表（真实遥测聚合）
  const energyChartOption = {
    tooltip: { trigger: 'axis' },
    legend: { data: ['园区负荷', '光伏出力', '储能充放电', '充电桩'] },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: trend?.labels || [] },
    yAxis: { type: 'value', name: 'kW' },
    series: [
      { name: '园区负荷', type: 'line', smooth: true, connectNulls: true, data: trend?.series?.load || [] },
      { name: '光伏出力', type: 'line', smooth: true, connectNulls: true, data: trend?.series?.pv || [] },
      { name: '储能充放电', type: 'bar', data: trend?.series?.storage || [] },
      { name: '充电桩', type: 'line', smooth: true, connectNulls: true, data: trend?.series?.charging || [] },
    ]
  };

  // 能耗组成饼图（基于当日真实电量，不再用瞬时功率×时长推算）
  const pvYield = today.total_pv_yield ?? 0;
  const gridEnergy = Math.max(0, (today.total_consumption ?? 0) - pvYield);
  const hasEnergyData = (today.total_consumption ?? 0) > 0 || pvYield > 0;
  const pieChartOption = {
    tooltip: { trigger: 'item', formatter: '{b}: {c} kWh ({d}%)' },
    series: [{
      type: 'pie', radius: ['40%', '70%'], center: ['50%', '50%'],
      data: [
        { name: '市电购入', value: Math.round(gridEnergy), itemStyle: { color: '#1890ff' } },
        { name: '光伏发电', value: Math.round(pvYield), itemStyle: { color: '#52c41a' } },
      ],
      label: { show: true, formatter: '{b}\n{d}%' }
    }]
  };

  // 告警表格
  const alarmColumns = [
    { title: '告警级别', dataIndex: 'level', render: l => {
      const colors = { info: 'blue', warning: 'orange', critical: 'red', emergency: 'purple' };
      return <Tag color={colors[l] || 'blue'}>{l?.toUpperCase()}</Tag>;
    }},
    { title: '告警内容', dataIndex: 'message' },
    { title: '时间', dataIndex: 'time', render: t => t },
  ];

  const alarmData = activeAlarms.map((a) => ({
    key: a.id,
    level: LEVEL_MAP[a.level] || 'info',
    message: a.title || a.content,
    time: a.occurred_at || a.created_at
      ? new Date(a.occurred_at || a.created_at).toLocaleString('zh-CN', { hour12: false })
      : '-',
  }));

  return (
    <div>
      <div style={{ marginBottom: 16, textAlign: 'right' }}>
        <Button type="primary" icon={<FullscreenOutlined />} onClick={() => navigate('/bigscreen')}>
          打开全景大屏
        </Button>
      </div>
      {/* 顶部指标卡 */}
      <Row gutter={[16, 16]}>
        <Col xs={12} sm={6}>
          <Card hoverable><Statistic title="总负荷" value={sv(overview.total_load)} suffix={ss(overview.total_load, 'kW')} prefix={<ThunderboltOutlined />} valueStyle={svStyle(overview.total_load, '#1890ff')} /></Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card hoverable><Statistic title="光伏功率" value={sv(overview.pv_power)} suffix={ss(overview.pv_power, 'kW')} prefix={<BulbOutlined />} valueStyle={svStyle(overview.pv_power, '#52c41a')} /></Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card hoverable><Statistic title="储能SOC" value={sv(overview.storage_soc)} suffix={ss(overview.storage_soc, '%')} prefix={<EnvironmentOutlined />}
            valueStyle={svStyle(overview.storage_soc, overview.storage_soc > 50 ? '#52c41a' : '#faad14')} /></Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card hoverable><Statistic title="充电桩功率" value={sv(overview.charging_power)} suffix={ss(overview.charging_power, 'kW')} prefix={<CarOutlined />} valueStyle={svStyle(overview.charging_power, '#722ed1')} /></Card>
        </Col>
      </Row>

      {/* 今日汇总 */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={12} sm={6}>
          <Card size="small"><Statistic title="今日总用电" value={today.total_consumption} precision={0} suffix="kWh" /></Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small"><Statistic title="光伏总发电" value={today.total_pv_yield} precision={0} suffix="kWh" valueStyle={{ color: '#52c41a' }} /></Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small"><Statistic title="CO₂减排" value={today.co2_reduction} precision={0} suffix="kg" valueStyle={{ color: '#52c41a' }} /></Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card size="small"><Statistic title="VPP可调容量" value={vpp.available_regulation} suffix="kW" valueStyle={{ color: '#1890ff' }} /></Card>
        </Col>
      </Row>

      {/* 图表行 */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={16}>
          <Card title="能源实时曲线 (24h)" size="small">
            {hasTrend
              ? <ReactEChartsCore option={energyChartOption} style={{ height: 300 }} />
              : <Empty description="暂无遥测数据" style={{ padding: '60px 0' }} />}
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title="能源组成（今日电量）" size="small">
            {hasEnergyData
              ? <ReactEChartsCore option={pieChartOption} style={{ height: 300 }} />
              : <Empty description="暂无能耗数据" style={{ padding: '60px 0' }} />}
          </Card>
        </Col>
      </Row>

      {/* 告警 + VPP */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card title="活跃告警" size="small">
            <Table columns={alarmColumns} dataSource={alarmData} pagination={false} size="small" locale={{ emptyText: '暂无活跃告警' }} />
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="VPP资源总览" size="small">
            <Row gutter={16}>
              <Col span={8}><Statistic title="总容量" value={vpp.total_capacity} suffix="kW" /></Col>
              <Col span={8}><Statistic title="上调容量" value={vpp.regulation_up_capacity} suffix="kW" valueStyle={{ color: '#faad14' }} /></Col>
              <Col span={8}><Statistic title="下调容量" value={vpp.regulation_down_capacity} suffix="kW" valueStyle={{ color: '#52c41a' }} /></Col>
            </Row>
            <div style={{ marginTop: 16, padding: '8px 16px', background: '#f5f5f5', borderRadius: 6 }}>
              <span>今日调度指令: <Tag color="blue">{vpp.dispatch_count} 条</Tag></span>
              <span style={{ marginLeft: 16 }}>自给率: <Tag color="green">{overview.self_sufficiency_rate == null ? '暂无' : `${overview.self_sufficiency_rate}%`}</Tag></span>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
